import { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { trackEvent } from 'analytics/trackFunctions'
import { MeContext } from 'contexts/me'
import {
  useCashoutResponse,
  useNewTransferStore,
  useSelectedAccountStore
} from 'store'
import { sendOTP } from 'api/OTP'

import { setCashout } from 'api/cashout'

import Skeleton from 'react-loading-skeleton'

import PageWrapper from 'components/PageWrapper'
import PrimaryButton from 'components/primaryButton'
import Card from 'components/Card'

import {
  accentFold,
  formatCUIL,
  moneyFormat,
  cleanString,
  toTitleCase
} from 'utils'

const ConfirmTransfer = () => {
  const [loading, setLoading] = useState<boolean>(false)

  const amount = useNewTransferStore((state) => state.amount)
  const concept = useNewTransferStore((state) => state.concept)
  const detail = useNewTransferStore((state) => state.detail)
  const selectedAccount = useSelectedAccountStore(
    (state) => state.selectedAccount
  )
  const entityData = useSelectedAccountStore((state) => state.entityData)

  const { updateResponse } = useCashoutResponse()

  const { me } = useContext(MeContext)

  const navigate = useNavigate()

  useEffect(() => {
    trackEvent({ eventName: 'bille_pantalla_nueva_transferencia_confirmacion' })
  }, [])

  const handleConfirmButton = () => {
    if (loading) return

    setLoading(true)

    const formattedDetail = cleanString(accentFold(detail))

    sendOTP().then(({ success, statusCode }) => {
      if (statusCode === 417) {
        // Chequeo de OTP si está activado o no
        cashoutCall(formattedDetail)
        return
      }

      if (!success) {
        navigate('/pedir-adelanto/error-otp')
        return
      }

      return navigate('/transferencias/validar', { replace: true })
    })
  }

  const cashoutCall = (formattedDetail: string) => {
    const data = {
      amount,
      tax_type: selectedAccount.CVU ? 'CVU' : 'CBU',
      tax_value: selectedAccount.CVU || selectedAccount.CBU,
      tax_identification_value: selectedAccount.CUIL,
      account_holder: selectedAccount.accountHolder,
      bank_name: entityData.bankName,
      concept: concept.name,
      description: formattedDetail
    }

    setCashout({
      ...data,
      wallet_internal_account: me.bank.internal_account[0].wallet_account
    })
      .then((res) => {
        if (res.success) {
          updateResponse(res.data)
          navigate('/transferencias/transferencia-exitosa')
        } else {
          navigate('/transferencias/error')
        }
      })
      .catch((error) => {
        console.log(error)
        trackEvent({
          eventName: 'bille_error_envio_transferencia',
          props: {
            ...data,
            apiError: JSON.stringify(error)
          }
        })
        navigate('/transferencias/error')
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <PageWrapper
      title='Validar transferencia'
      noTabBar
      viewSelected='/movimientos'
    >
      <section className='flex flex-col text-center text-black items-center mx-auto justify-center sm:max-w-[22.5rem]'>
        <p className='text-4xl'>
          {me ? (
            toTitleCase(selectedAccount.accountHolder)
          ) : (
            <Skeleton height={48} />
          )}
        </p>
        <p className='text-4xl font-bold'>{moneyFormat(amount)}</p>
        <Card className='w-full bg-white py-2 px-4 mt-4'>
          <ul className='w-full text-left divide-y-2 divide-gray-500'>
            <ListItem name='Origen' value={me?.bank?.internal_account[0].cvu} />
            <ListItem
              name='Destino'
              value={selectedAccount.CVU || selectedAccount.CBU}
            />
            <ListItem
              name='CUIL'
              value={formatCUIL(selectedAccount.CUIL.toString())}
            />
            <ListItem
              name='Banco'
              value={
                <span className='flex gap-1'>
                  {!!entityData.iconURL && (
                    <>
                      <img
                        className='rounded-full h-5 w-5'
                        src={entityData.iconURL}
                        alt=''
                      />{' '}
                    </>
                  )}
                  {entityData.bankName}
                </span>
              }
            />
            <ListItem name='Motivo' value={concept.name} />
            <ListItem name='Detalle' value={detail} />
          </ul>
        </Card>
        <PrimaryButton
          className='mt-8'
          buttonText='Continuar'
          isLoading={loading}
          primaryButtonFunction={() => {
            trackEvent({
              eventName: 'bille_nueva_transferencia_boton_confirmar'
            })
            handleConfirmButton()
          }}
        />
        <PrimaryButton
          outlined
          className='mt-4 mb-10'
          buttonText='Cancelar'
          primaryButtonFunction={() =>
            navigate('/transferencias/nueva-transferencia')
          }
        />
      </section>
    </PageWrapper>
  )
}

export default ConfirmTransfer

export const ListItem = ({
  name,
  value
}: {
  name: string
  value: string | JSX.Element
}) => (
  <li className='text-xs gap-1 font-medium py-4 flex'>
    <span>{name}:</span> <span className='font-bold'> {value}</span>
  </li>
)
